import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../hooks/useUsers";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherPlus,
  FeatherTrash2,
  FeatherEdit,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherSearch,
} from "@subframe/core";

export const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const { users, pagination, loading, error, fetchUsers, deleteUser } =
    useUsers();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when user starts typing (searchTerm changes)
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setPage(1);
    }
  }, [searchTerm, debouncedSearchTerm]);

  // Fetch users when page or debounced search term changes
  useEffect(() => {
    fetchUsers(page, 50, debouncedSearchTerm || undefined);
  }, [page, debouncedSearchTerm, fetchUsers]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      setDeletingUserId(id);
      await deleteUser(id);
    } catch (err: any) {
      alert(err.message || "Failed to delete user");
    } finally {
      setDeletingUserId(null);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-6 p-12">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Users
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage all users across all companies
          </span>
        </div>
        <Button
          icon={<FeatherPlus />}
          onClick={() => navigate("/admin/users/create")}
        >
          Create User
        </Button>
      </div>

      {error && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">
            {error.message}
          </span>
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-4">
        <TextField
          className="w-full max-w-md"
          variant="filled"
          label=""
          helpText=""
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search by email, username, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </TextField>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Username</Table.HeaderCell>
              <Table.HeaderCell>Companies</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {users.map((user) => (
            <Table.Row key={user.id}>
              <Table.Cell>
                <div className="flex items-center gap-2">
                  <span className="text-body font-body text-default-font">
                    {user.email}
                  </span>
                  {user.is_master_admin && (
                    <Badge variant="brand" size="small">
                      Master Admin
                    </Badge>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <span className="text-body font-body text-subtext-color">
                  {user.userName || "-"}
                </span>
              </Table.Cell>
              <Table.Cell>
                {user.companies && user.companies.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {user.companies.map((company) => (
                      <span key={company.id} className="text-xs">
                        {company.company_name}
                        {company.role_name && (
                          <span className="ml-1 text-gray-400">
                            ({company.role_name})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-body font-body text-subtext-color">
                    -
                  </span>
                )}
              </Table.Cell>
              <Table.Cell>
                <Badge variant={user.is_active ? "success" : "error"}>
                  {user.is_active ? "Active" : "Inactive"}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                {!user.is_master_admin && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherEdit />}
                      onClick={() => navigate(`/admin/users/update/${user.id}`)}
                    >
                      Edit
                    </Button>
                    {user.is_active && (
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        icon={<FeatherTrash2 />}
                        onClick={() => handleDelete(user.id)}
                        loading={deletingUserId === user.id}
                        disabled={deletingUserId === user.id || loading}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherChevronLeft />}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-body font-body text-default-font px-4">
              Page {page} of {pagination.total_pages}
            </span>
            <Button
              variant="neutral-secondary"
              size="small"
              iconRight={<FeatherChevronRight />}
              onClick={() =>
                setPage((p) => Math.min(pagination.total_pages, p + 1))
              }
              disabled={page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

